// Stage 1 — Find a Preprint (FR-1.*, July-7 FR-K1–K5). Discovery defaults to the Unclaimed
// (Unread) pool across ALL domains for every role; July-8 Bucket 2 adds richer cards, multi-domain
// filter, and human-facing Unclaimed/Claimed labels while API values stay verbatim.
import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  SELECTED_STATUSES,
  DOMAIN_LABELS,
  RRID_COUNTRY_TEAMS,
  selectedStatusLabel,
  type Domain,
  type SelectedStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, EmptyState, Input, Modal, Select, Spinner, statusTone } from "../../components/ui.js";
import { MultiFilter } from "../../components/MultiFilter.js";
import { confirmAdvance } from "./advance-confirms.js";
import { useProductEvent } from "../../lib/telemetry.js";

const NEXT_STATUS: Partial<Record<SelectedStatus, { status: SelectedStatus; label: string }>> = {
  Taken: { status: "To Pitch(Team)", label: "Ready to pitch (Team)" },
  "To Pitch(Team)": { status: "To Pitch(Editorial)", label: "Advance to Editorial" },
  "To Pitch(Editorial)": { status: "Selected", label: "Set Selected" },
};

const REFINE_STATUSES: SelectedStatus[] = ["To Pitch(Team)", "To Pitch(Editorial)"];
const COMBINED = "editorial+selected";
const ALL = "all";
type StatusFilter = SelectedStatus | typeof COMBINED | typeof ALL;

function toggleDomain(current: Domain[], d: Domain): Domain[] {
  return current.includes(d) ? current.filter((x) => x !== d) : [...current, d];
}

function parseStatusFilter(raw: string | null): StatusFilter {
  if (raw === ALL || raw === COMBINED) return raw;
  if (raw && (SELECTED_STATUSES as readonly string[]).includes(raw)) return raw as SelectedStatus;
  return "Unread";
}

function parseDomains(raw: string | null): Domain[] {
  if (!raw) return [];
  return raw.split(",").filter((d): d is Domain => (DOMAINS as readonly string[]).includes(d));
}

/** Fetches the FULL abstract on demand (list only carries a blurb). */
function FullAbstract({ id, fallback }: { id: string; fallback: string | null }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: qk.selectedOne(id),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${id}`),
  });
  if (isLoading) return <span className="text-slate-400">Loading abstract…</span>;
  const full = data?.abstract?.trim() || fallback;
  return <>{full || "No abstract on file for this preprint."}</>;
}

/** Closed Abstract disclosure (same pattern as My Quality Checks); opens to the full text. */
function AbstractDisclosure({ id, blurb }: { id: string; blurb: string | null }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!blurb?.trim()) return null;
  return (
    <details
      className="mt-1"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
        Abstract
      </summary>
      {open ? (
        <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">
          <FullAbstract id={id} fallback={blurb} />
        </p>
      ) : null}
    </details>
  );
}

export function PreprintsPortal(): JSX.Element {
  useProductEvent("ui.preprints_opened", { path: "/preprints" });
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const domains = parseDomains(searchParams.get("domains"));
  const status = parseStatusFilter(searchParams.get("status"));
  const mine = searchParams.get("mine") === "1";
  // WO-7/21 Track D: no gates-only tag/filter — Gates-funded preprints still auto-prioritize
  // (the server sorts them first); Track I: the team filter is a multi-select.
  const teamsSel = (searchParams.get("teams") ?? "").split(",").filter(Boolean);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [claimConfirm, setClaimConfirm] = useState<SelectedView | null>(null);
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");

  const writeFilters = (next: {
    domains?: Domain[];
    status?: StatusFilter;
    mine?: boolean;
    teams?: string[];
    q?: string;
  }): void => {
    const d = next.domains ?? domains;
    const st = next.status ?? status;
    const m = next.mine ?? mine;
    const t = next.teams ?? teamsSel;
    const q = next.q !== undefined ? next.q : search;
    const p = new URLSearchParams();
    if (d.length) p.set("domains", d.join(","));
    if (st !== "Unread") p.set("status", st);
    if (m) p.set("mine", "1");
    if (t.length) p.set("teams", t.join(","));
    if (q.trim()) p.set("q", q.trim());
    setSearchParams(p, { replace: true });
  };

  const params = new URLSearchParams();
  if (domains.length) params.set("domains", domains.join(","));
  if (status === COMBINED) params.set("statuses", "To Pitch(Editorial),Selected");
  else params.set("status", status);
  if (mine) params.set("mine", "1");
  if (teamsSel.length) params.set("teams", teamsSel.join(","));
  const key = Object.fromEntries(params);

  const { data, isLoading } = useQuery({
    queryKey: qk.selected(key),
    queryFn: () => api.get<SelectedView[]>(`/preprints/selected?${params.toString()}`),
  });

  const q = search.trim().toLowerCase();
  const rows = (data ?? []).filter((s) => {
    if (!q) return true;
    return `${s.title} ${s.authors ?? ""}`.toLowerCase().includes(q);
  });

  const assign = useMutation({
    mutationFn: (id: string) => api.post<SelectedView>(`/preprints/selected/${id}/assign`),
    onSuccess: (updated) => {
      setAssignError(null);
      qc.invalidateQueries({ queryKey: ["selected"] });
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
      navigate(`/preprints/${updated.id}`, { state: { from: location.search } });
    },
    onError: (err) => {
      const body = err instanceof ApiError && err.body && typeof err.body === "object" ? (err.body as { error?: unknown }) : null;
      setAssignError(typeof body?.error === "string" ? body.error : "Could not assign this preprint.");
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
    },
  });
  const advance = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SelectedStatus }) =>
      api.post(`/preprints/selected/${id}/advance`, { status, aeChecks: { confirmed: true } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["selected"] });
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
    },
  });
  const unclaim = useMutation({
    mutationFn: (id: string) => api.post(`/preprints/selected/${id}/unclaim`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["selected"] });
      qc.invalidateQueries({ queryKey: ["assignment-slots"] });
    },
  });
  const setUrgent = useMutation({
    mutationFn: ({ id, urgent }: { id: string; urgent: boolean }) => api.post(`/preprints/selected/${id}/urgent`, { urgent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });
  const refine = useMutation({
    mutationFn: (id: string) => api.post(`/preprints/selected/${id}/refine`, { mode: "reviewers" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });
  const rankInput = useMutation({
    mutationFn: ({ id, rank }: { id: string; rank: number }) => api.post(`/preprints/selected/${id}/rank-input`, { rank }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });
  const correctDomain = useMutation({
    mutationFn: ({ id, domain }: { id: string; domain: Domain }) => api.patch(`/preprints/selected/${id}`, { domain }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Find a Preprint</h1>
          <p className="text-sm text-slate-500">
            Hunt new (Unclaimed) preprints in any domain, assign one to yourself, and pitch it from its Details page.
          </p>
          {/* WO-7/23 Track A [07:05–08:07]: the slot counter is hidden here too. The cap is still
              enforced server-side; a 4th claim raises the explanatory pop-up below. */}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-56">
            <Input
              type="search"
              placeholder="Search title or author…"
              className="py-1.5"
              value={search}
              onChange={(e) => {
                const v = e.target.value;
                setSearch(v);
                writeFilters({ q: v });
              }}
              aria-label="Search preprints by title or author"
            />
          </span>
          <details className="group relative">
            <summary className="cursor-pointer list-none rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              Domains: {domains.length ? domains.join(", ") : "All"}
            </summary>
            <div className="absolute right-0 z-10 mt-1 min-w-[12rem] rounded-md border border-slate-200 bg-white p-2 shadow-lg">
              <p className="mb-1 text-xs font-medium text-slate-500">Select one or more</p>
              {DOMAINS.map((d) => (
                <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={domains.includes(d)}
                    onChange={() => writeFilters({ domains: toggleDomain(domains, d) })}
                  />
                  <span>
                    {d} <span className="text-xs text-slate-400">({DOMAIN_LABELS[d]})</span>
                  </span>
                </label>
              ))}
              {domains.length > 0 && (
                <button type="button" className="mt-1 text-xs text-brand hover:underline" onClick={() => writeFilters({ domains: [] })}>
                  Clear filter
                </button>
              )}
            </div>
          </details>
          <Select
            value={status}
            onChange={(e) => writeFilters({ status: e.target.value as StatusFilter })}
          >
            <option value={ALL}>All statuses</option>
            <option value={COMBINED}>Editorial + Selected</option>
            {SELECTED_STATUSES.map((s) => (
              <option key={s} value={s}>
                {selectedStatusLabel(s)}
              </option>
            ))}
          </Select>
          <MultiFilter label="Country / Team" options={RRID_COUNTRY_TEAMS} value={teamsSel} onChange={(teams) => writeFilters({ teams })} />
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => {
                const next = e.target.checked;
                writeFilters({ mine: next, status: next && status === "Unread" ? ALL : status });
              }}
            />{" "}
            My Preprints
          </label>
        </div>
      </header>

      {/* WO-7/23 Track A: with the counter hidden, hitting the cap has to be unmissable — a
          modal, not an inline banner that scrolls out of view. */}
      {assignError && (
        <Modal open onClose={() => setAssignError(null)} title="You've reached your preprint limit">
          <div className="space-y-3">
            <p className="text-sm text-slate-700">{assignError}</p>
            <div className="flex justify-end">
              <Button onClick={() => setAssignError(null)}>Got it</Button>
            </div>
          </div>
        </Modal>
      )}

      {isLoading ? (
        <Spinner label="Loading preprints…" />
      ) : rows.length === 0 ? (
        <EmptyState>
          {q ? `No preprints match “${search.trim()}”.` : "No preprints match these filters."}
        </EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-[13px]">
              <thead>
                <tr className="bg-slate-700 text-left text-[11px] font-semibold uppercase tracking-wide text-white">
                  <th className="px-4 py-2.5">Preprint</th>
                  <th className="px-3 py-2.5">Domain</th>
                  <th className="px-3 py-2.5">Authors</th>
                  <th className="whitespace-nowrap px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const next = NEXT_STATUS[s.status];
                  const ctx = { domain: s.domain, beforePitching: s.status === "Taken" };
                  const canCorrectUnclaimedDomain =
                    s.status === "Unread" && can("correctDomain", { domain: s.domain, beforePitching: false });
                  const own =
                    !!user && !!s.assigneeEmail && s.assigneeEmail.toLowerCase() === user.email.toLowerCase();
                  const advanceAction =
                    s.status === "Taken"
                      ? "setToPitchTeam"
                      : s.status === "To Pitch(Team)"
                        ? "advanceToPitchEditorial"
                        : "setToSelected";
                  const canAdvance =
                    !!next &&
                    (advanceAction === "setToPitchTeam" ? own || can(advanceAction, ctx) : can(advanceAction, {}));
                  const canUnclaim = s.status === "Taken" && (own || can("advanceToPitchEditorial", {}));
                  const rejectTarget: SelectedStatus | null =
                    s.status === "Unread"
                      ? "Rejected Once"
                      : s.status === "To Pitch(Team)"
                        ? "Team Rejected"
                        : s.status === "To Pitch(Editorial)"
                          ? "Editorial Rejected"
                          : null;
                  const canReject =
                    !!rejectTarget &&
                    (s.status === "Unread"
                      ? can("rejectFromPool", {})
                      : can(advanceAction, {}));
                  const isUrgent = (s.pitch as Record<string, unknown>).Urgent === "Urgent";
                  const titleEl = s.doi ? (
                    <a className="font-medium text-brand hover:underline" href={s.doi} target="_blank" rel="noreferrer">
                      {s.title} ↗
                    </a>
                  ) : (
                    <span className="font-medium text-slate-800">{s.title}</span>
                  );
                  return (
                    <tr key={s.id} className="border-b border-slate-100 align-top last:border-b-0 hover:bg-slate-50">
                      <td className="max-w-md px-4 py-3">
                        <div className="leading-snug">{titleEl}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {s.team && <Badge tone="violet">{s.team}</Badge>}
                          {isUrgent && <Badge tone="red">Urgent</Badge>}
                          {s.proposalStatus && <Badge tone="blue">{s.proposalStatus}</Badge>}
                          {s.avgRanking != null && <Badge tone="blue">avg rank {s.avgRanking}</Badge>}
                          {s.altmetricScore != null && (
                            <Badge tone="violet">
                              Altmetric{" "}
                              {Number.isInteger(s.altmetricScore) ? s.altmetricScore : s.altmetricScore.toFixed(1)}
                            </Badge>
                          )}
                        </div>
                        <AbstractDisclosure id={s.id} blurb={s.abstractBlurb} />
                        <p className="mt-1 text-[11px] text-slate-400">
                          {s.assigneeEmail ? (
                            <>
                              {s.assigneeName ? `${s.assigneeName} · ` : ""}
                              <a className="text-brand hover:underline" href={`mailto:${s.assigneeEmail}`}>
                                {s.assigneeEmail}
                              </a>
                            </>
                          ) : (
                            "Unassigned"
                          )}
                          {(s.aeAssigneeName || s.aeAssigneeEmail) && (
                            <>
                              {" · AE: "}
                              {s.aeAssigneeName ?? s.aeAssigneeEmail}
                            </>
                          )}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-slate-700">
                        {canCorrectUnclaimedDomain ? (
                          <Select
                            aria-label="Change scraping-assigned domain"
                            className="max-w-[9rem]"
                            value={s.domain ?? ""}
                            disabled={correctDomain.isPending}
                            onChange={(e) => {
                              const next = e.target.value as Domain;
                              if (next && next !== s.domain) correctDomain.mutate({ id: s.id, domain: next });
                            }}
                          >
                            {!s.domain && <option value="">—</option>}
                            {DOMAINS.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          (s.domain ?? "—")
                        )}
                      </td>
                      <td className="max-w-[14rem] px-3 py-3 text-slate-600">
                        <span className="line-clamp-3">{s.authors?.trim() || "—"}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-slate-500">{s.publishedDate ?? "—"}</td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge tone={statusTone(s.status)}>{selectedStatusLabel(s.status)}</Badge>
                          {s.gatesFunded && <Badge tone="amber">Gates</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          {s.status === "Unread" && can("assignPreprint") && (
                            <Button onClick={() => setClaimConfirm(s)} disabled={assign.isPending}>
                              {assign.isPending ? "Assigning…" : "Assign to me"}
                            </Button>
                          )}
                          {s.status !== "Unread" && (
                            <Link to={`/preprints/${s.id}`} state={{ from: location.search }}>
                              <Button variant="secondary">Details</Button>
                            </Link>
                          )}
                          {canAdvance && next && (
                            <Button
                              onClick={() => {
                                if (!confirmAdvance(next.status)) return;
                                advance.mutate({ id: s.id, status: next.status });
                              }}
                              disabled={advance.isPending}
                            >
                              {next.label}
                            </Button>
                          )}
                          {canUnclaim && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                if (!window.confirm("Return this preprint to Unclaimed?")) return;
                                unclaim.mutate(s.id);
                              }}
                              disabled={unclaim.isPending}
                            >
                              {unclaim.isPending ? "Unclaiming…" : "Unclaim"}
                            </Button>
                          )}
                          {canReject && rejectTarget && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    s.status === "Unread"
                                      ? "Reject this unclaimed preprint? It leaves the Unclaimed pool (Rejected Once)."
                                      : `Reject this preprint at the ${selectedStatusLabel(s.status)} gate?`,
                                  )
                                ) {
                                  advance.mutate({ id: s.id, status: rejectTarget });
                                }
                              }}
                              disabled={advance.isPending}
                            >
                              Reject
                            </Button>
                          )}
                          {s.status !== "Unread" && (own || can("editPitchDeck", ctx)) && (
                            <Button
                              variant="ghost"
                              onClick={() => setUrgent.mutate({ id: s.id, urgent: !isUrgent })}
                              disabled={setUrgent.isPending}
                            >
                              {isUrgent ? "Unset urgent" : "Set urgent"}
                            </Button>
                          )}
                          {REFINE_STATUSES.includes(s.status) && can("editSimilarArticles", ctx) && (
                            <Button variant="ghost" onClick={() => refine.mutate(s.id)} disabled={refine.isPending}>
                              Refine reviewers
                            </Button>
                          )}
                          {/* FR-P13: no Input ranking on Unclaimed — even for AE/Manager. */}
                          {s.status !== "Unread" && can("inputRanking", ctx) && (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                const cur = user ? s.rankings[user.email.toLowerCase()] : undefined;
                                const v = window.prompt(
                                  "Your ranking (1 = highest priority):",
                                  cur != null ? String(cur) : "",
                                );
                                const n = v == null ? NaN : Number.parseInt(v, 10);
                                if (Number.isInteger(n) && n > 0) rankInput.mutate({ id: s.id, rank: n });
                              }}
                              disabled={rankInput.isPending}
                            >
                              Input ranking
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {claimConfirm && (
        <Modal open onClose={() => setClaimConfirm(null)} title="Claim this preprint?">
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to claim this preprint?
            </p>
            <p className="text-sm font-medium text-slate-800">{claimConfirm.title}</p>
            <p className="text-xs text-slate-500">
              It will be assigned to you and the pitch deck opens so you can start working on it.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setClaimConfirm(null)} disabled={assign.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const id = claimConfirm.id;
                  setClaimConfirm(null);
                  assign.mutate(id);
                }}
                disabled={assign.isPending}
              >
                {assign.isPending ? "Assigning…" : "Yes, claim it"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
